import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { apiRequest } from "../config/api";
import { AuthResponse } from "../types";
import { useOrganization } from "./OrganizationContext";

interface AuthContextType {
  user: AuthResponse["user"] | null;
  token: string | null;
  login: (
    username: string,
    password: string,
    role?: "admin" | "scorekeeper",
  ) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  isAdmin: boolean;
  isScorekeeper: boolean;

  // Player authentication
  playerUser: AuthResponse["user"] | null;
  playerToken: string | null;
  playerLogin: (username: string, password: string) => Promise<boolean>;
  playerLogout: () => void;
  isPlayer: boolean;

  // Unified login for all account types
  unifiedLogin: (
    username: string,
    password: string,
  ) => Promise<{ success: boolean; error?: string }>;

  // Admin impersonation
  impersonatePlayer: (playerId: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { currentOrg } = useOrganization();
  const [user, setUser] = useState<AuthResponse["user"] | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [playerUser, setPlayerUser] = useState<AuthResponse["user"] | null>(
    null,
  );
  const [playerToken, setPlayerToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Helper function to check if token is expired
  const isTokenExpired = (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= expirationTime;
    } catch (error) {
      console.error("Error checking token expiration:", error);
      return true; // Treat invalid tokens as expired
    }
  };

  // Define logout functions first so they can be used in useEffect
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    localStorage.removeItem("loginTime");
  };

  const playerLogout = () => {
    setPlayerUser(null);
    setPlayerToken(null);
    localStorage.removeItem("playerAuthToken");
    localStorage.removeItem("playerAuthUser");
    localStorage.removeItem("playerLoginTime");
  };

  useEffect(() => {
    // Check for existing tokens in localStorage on app load
    const savedToken = localStorage.getItem("authToken");
    const savedUser = localStorage.getItem("authUser");
    const savedPlayerToken = localStorage.getItem("playerAuthToken");
    const savedPlayerUser = localStorage.getItem("playerAuthUser");

    // Validate admin/scorekeeper token
    if (savedToken && savedUser) {
      if (isTokenExpired(savedToken)) {
        console.log("Admin token expired, clearing session");
        localStorage.removeItem("authToken");
        localStorage.removeItem("authUser");
        localStorage.removeItem("loginTime");
      } else {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      }
    }

    // Validate player token
    if (savedPlayerToken && savedPlayerUser) {
      if (isTokenExpired(savedPlayerToken)) {
        console.log("Player token expired, clearing session");
        localStorage.removeItem("playerAuthToken");
        localStorage.removeItem("playerAuthUser");
        localStorage.removeItem("playerLoginTime");
        // Set flag for login page to show message
        sessionStorage.setItem("sessionExpired", "true");
      } else {
        const parsedPlayerUser = JSON.parse(savedPlayerUser);
        let shouldClearSession = false;

        // Validate that the player belongs to the current organization
        // The token will be validated server-side, but we clear it client-side for UX
        try {
          // Decode JWT to check orgId (without verification, just for client-side check)
          const tokenParts = savedPlayerToken.split(".");
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));

            // If token has orgId and we have currentOrg, verify they match
            if (
              payload.orgId &&
              currentOrg &&
              payload.orgId !== currentOrg.id
            ) {
              console.log(
                `Player token org mismatch: token has ${payload.orgId}, current org is ${currentOrg.id}`,
              );
              shouldClearSession = true;
            }
            // If token doesn't have orgId, it's an old token - clear it
            else if (!payload.orgId) {
              console.log(
                "Player token missing orgId (old token), clearing session",
              );
              shouldClearSession = true;
            }
          }
        } catch (err) {
          console.error("Error validating player token org:", err);
          shouldClearSession = true;
        }

        if (shouldClearSession) {
          localStorage.removeItem("playerAuthToken");
          localStorage.removeItem("playerAuthUser");
          localStorage.removeItem("playerLoginTime");
        } else {
          setPlayerToken(savedPlayerToken);
          setPlayerUser(parsedPlayerUser);
        }
      }
    }

    setIsLoading(false);

    // Set up periodic token validation (check every 5 minutes)
    const tokenCheckInterval = setInterval(
      () => {
        const currentPlayerToken = localStorage.getItem("playerAuthToken");
        const currentAdminToken = localStorage.getItem("authToken");

        // Check player token
        if (currentPlayerToken && isTokenExpired(currentPlayerToken)) {
          console.log("Player token expired during session, logging out");
          sessionStorage.setItem("sessionExpired", "true");
          playerLogout();
          if (window.location.pathname.includes("/player/")) {
            window.location.href = "/player/login";
          }
        }

        // Check admin token
        if (currentAdminToken && isTokenExpired(currentAdminToken)) {
          console.log("Admin token expired during session, logging out");
          logout();
          if (!window.location.pathname.includes("/player/")) {
            window.location.href = "/login";
          }
        }
      },
      5 * 60 * 1000,
    ); // Check every 5 minutes

    return () => clearInterval(tokenCheckInterval);
  }, [currentOrg]);

  const login = async (
    username: string,
    password: string,
    role: "admin" | "scorekeeper" = "admin",
  ): Promise<boolean> => {
    try {
      const endpoint =
        role === "admin" ? "/api/auth/login" : "/api/scorekeepers/login";

      const response = await apiRequest(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const authData: AuthResponse = await response.json();
        setUser(authData.user);
        setToken(authData.token);

        // Save to localStorage with login timestamp
        localStorage.setItem("authToken", authData.token);
        localStorage.setItem("authUser", JSON.stringify(authData.user));
        localStorage.setItem("loginTime", new Date().toISOString());

        return true;
      }
      return false;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  const playerLogin = async (
    username: string,
    password: string,
  ): Promise<boolean> => {
    try {
      const response = await apiRequest("/api/player/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const authData: AuthResponse = await response.json();
        setPlayerUser(authData.user);
        setPlayerToken(authData.token);

        // Save to localStorage
        localStorage.setItem("playerAuthToken", authData.token);
        localStorage.setItem("playerAuthUser", JSON.stringify(authData.user));
        localStorage.setItem("playerLoginTime", new Date().toISOString());

        return true;
      }

      // Read error response
      const errorData = await response.json();
      throw new Error(errorData.error || "Invalid username or password");
    } catch (error: any) {
      console.error("Player login error:", error);
      throw error;
    }
  };

  const unifiedLogin = async (
    username: string,
    password: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await apiRequest("/api/unified-auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const authData: AuthResponse = await response.json();

        // Check if it's a player or admin/scorekeeper based on role
        if (authData.user.role === "player") {
          setPlayerUser(authData.user);
          setPlayerToken(authData.token);
          localStorage.setItem("playerAuthToken", authData.token);
          localStorage.setItem("playerAuthUser", JSON.stringify(authData.user));
          localStorage.setItem("playerLoginTime", new Date().toISOString());
        } else {
          // Admin or scorekeeper
          setUser(authData.user);
          setToken(authData.token);
          localStorage.setItem("authToken", authData.token);
          localStorage.setItem("authUser", JSON.stringify(authData.user));
          localStorage.setItem("loginTime", new Date().toISOString());
        }

        return { success: true };
      }

      // Get error message from response
      try {
        const data = await response.json();
        console.log("[Unified Login] Error response data:", data);
        return {
          success: false,
          error: data.error || data.message || "Invalid username or password",
        };
      } catch (jsonError) {
        console.error("Error parsing error response:", jsonError);
        return { success: false, error: "Invalid username or password" };
      }
    } catch (error) {
      console.error("Unified login error:", error);
      return { success: false, error: "Login failed. Please try again." };
    }
  };

  const impersonatePlayer = async (
    playerId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const adminToken = localStorage.getItem("authToken");
      if (!adminToken) {
        return { success: false, error: "Admin session required" };
      }

      const response = await apiRequest(`/api/player/impersonate/${playerId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const authData: AuthResponse = await response.json();
        setPlayerUser(authData.user);
        setPlayerToken(authData.token);
        localStorage.setItem("playerAuthToken", authData.token);
        localStorage.setItem("playerAuthUser", JSON.stringify(authData.user));
        localStorage.setItem("playerLoginTime", new Date().toISOString());
        localStorage.setItem("impersonating", "true");
        return { success: true };
      }

      const data = await response.json();
      return { success: false, error: data.error || "Failed to impersonate player" };
    } catch (error) {
      console.error("Impersonation error:", error);
      return { success: false, error: "Failed to impersonate player" };
    }
  };

  const isAdmin = !!user && (user.role === "admin" || !user.role); // User must exist and be admin
  const isScorekeeper = !!user && user.role === "scorekeeper"; // User must exist and be scorekeeper
  const isPlayer = !!playerUser && playerUser.role === "player"; // Player must exist and be player

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        isLoading,
        isAdmin,
        isScorekeeper,
        playerUser,
        playerToken,
        playerLogin,
        playerLogout,
        isPlayer,
        unifiedLogin,
        impersonatePlayer,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
